import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { formatDateDMY } from '../utils/dateFormat';

/**
 * Dashboard widget — every PO + Sales Invoice waiting on THIS user's
 * approval AND every cancellation request waiting on THIS user's decision.
 *
 * Cancellation requests share the same widget on purpose: there is no
 * separate "Cancellation Requests" menu. Clicking a cancellation row
 * opens a small modal with the reason + Approve / Reject Cancellation
 * buttons.
 */
const DOC_KIND_LABEL = {
  sales_invoice:  'Sales Invoice',
  purchase_order: 'Purchase Order',
  delivery_note:  'Delivery Note',
  payment:        'Payment',
};

export default function PendingApprovalsWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCancel, setActiveCancel] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/approvals/my-pending');
      setData(res.data);
    } catch (e) {
      setData({ purchase_orders: [], sales_invoices: [], cancellation_requests: [], total: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const approveCancel = async () => {
    if (!activeCancel) return;
    if (!window.confirm('Approve this cancellation? The document will be permanently cancelled and stock movements reversed.')) return;
    setBusy(true);
    try {
      await api.post(`/cancellation-requests/${activeCancel.id}/approve`);
      setActiveCancel(null);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Approve failed');
    } finally { setBusy(false); }
  };

  const rejectCancel = async () => {
    if (!activeCancel) return;
    if (rejectReason.trim().length < 3) {
      alert('Rejection reason must be at least 3 characters.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/cancellation-requests/${activeCancel.id}/reject`, { reason: rejectReason.trim() });
      setActiveCancel(null);
      setRejectReason('');
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Reject failed');
    } finally { setBusy(false); }
  };

  if (loading || !data || data.total === 0) return null;

  const cancellations = data.cancellation_requests || [];

  return (
    <div className="bg-white border-2 border-amber-300 rounded-lg shadow-sm p-4 mb-4" data-testid="pending-approvals-widget">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-lg inline-flex items-center gap-2">
          ⏳ My Pending Approvals
          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
            {data.total}
          </span>
        </h3>
        <button onClick={load} className="text-xs text-blue-700 hover:underline" title="Refresh">↻ Refresh</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Purchase Orders */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1.5">
            🧾 Purchase Orders ({data.purchase_orders.length})
          </div>
          {data.purchase_orders.length === 0 ? (
            <div className="text-xs text-gray-400 italic px-2 py-3">None.</div>
          ) : (
            <ul className="divide-y border rounded">
              {data.purchase_orders.map((po) => (
                <li key={po.id} className="px-3 py-2 hover:bg-amber-50/40" data-testid={`pending-po-${po.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{po.po_number}</div>
                      <div className="text-xs text-gray-600 truncate">
                        {po.supplier_name} · {formatDateDMY(po.po_date)}
                      </div>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <div className="text-sm font-semibold">
                        {(po.total_amount || 0).toFixed(2)} {po.currency}
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        po.approval_status === 'pending_l1' ? 'bg-orange-100 text-orange-800' : 'bg-purple-100 text-purple-800'
                      }`}>
                        {po.approval_status === 'pending_l1' ? 'Level 1' : 'Level 2'}
                      </span>
                    </div>
                  </div>
                  <Link to="/purchase-orders" className="inline-block mt-1 text-xs text-blue-700 hover:underline">Open PO →</Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Sales Invoices */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1.5">
            💰 Sales Invoices ({data.sales_invoices.length})
          </div>
          {data.sales_invoices.length === 0 ? (
            <div className="text-xs text-gray-400 italic px-2 py-3">None.</div>
          ) : (
            <ul className="divide-y border rounded">
              {data.sales_invoices.map((inv) => (
                <li key={inv.invoice_number} className="px-3 py-2 hover:bg-amber-50/40" data-testid={`pending-invoice-${inv.invoice_number}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{inv.invoice_number}</div>
                      <div className="text-xs text-gray-600 truncate">
                        {inv.customer_name} · {formatDateDMY(inv.invoice_date)}
                      </div>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <div className="text-sm font-semibold">
                        {(inv.total_amount || 0).toFixed(2)} {inv.currency}
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        inv.approval_status === 'pending_l1' ? 'bg-orange-100 text-orange-800' : 'bg-purple-100 text-purple-800'
                      }`}>
                        {inv.approval_status === 'pending_l1' ? 'Level 1' : 'Level 2'}
                      </span>
                    </div>
                  </div>
                  <Link to="/sales-invoices" className="inline-block mt-1 text-xs text-blue-700 hover:underline">Open Invoice →</Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Cancellation Requests — full-width row */}
        {cancellations.length > 0 && (
          <div className="md:col-span-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1.5">
              🗑 Cancellation Requests ({cancellations.length})
            </div>
            <ul className="divide-y border rounded" data-testid="pending-cancellations-list">
              {cancellations.map((req) => (
                <li
                  key={req.id}
                  className="px-3 py-2 hover:bg-rose-50/40 cursor-pointer"
                  onClick={() => { setActiveCancel(req); setRejectReason(''); }}
                  data-testid={`pending-cancellation-${req.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">
                        {DOC_KIND_LABEL[req.doc_kind] || req.doc_kind} · {req.doc_summary?.ref || req.doc_id}
                      </div>
                      <div className="text-xs text-gray-600 truncate">
                        {req.doc_summary?.party || '—'} ·
                        Requested by <span className="font-medium">{req.requested_by_name || '—'}</span>
                      </div>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <div className="text-sm font-semibold">
                        {req.doc_summary?.amount != null ? Number(req.doc_summary.amount).toFixed(2) : ''}
                      </div>
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-rose-100 text-rose-800">
                        🟠 Cancel
                      </span>
                    </div>
                  </div>
                  <div className="mt-1 text-xs text-blue-700 hover:underline">View → Approve / Reject</div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Cancellation review modal */}
      {activeCancel && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
             onClick={() => !busy && setActiveCancel(null)}>
          <div className="bg-white rounded-lg max-w-lg w-full p-5 space-y-3"
               onClick={(e) => e.stopPropagation()} data-testid="cancellation-review-modal">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-rose-700 font-semibold">Cancellation Request</div>
                <div className="text-lg font-semibold">{DOC_KIND_LABEL[activeCancel.doc_kind] || activeCancel.doc_kind}: {activeCancel.doc_summary?.ref || activeCancel.doc_id}</div>
              </div>
              <button onClick={() => setActiveCancel(null)} className="text-slate-500" aria-label="close">✕</button>
            </div>

            <div className="text-sm space-y-1 bg-slate-50 border rounded p-3">
              {activeCancel.doc_summary?.party && (
                <div><span className="text-slate-500">Party:</span> <span className="font-medium">{activeCancel.doc_summary.party}</span></div>
              )}
              {activeCancel.doc_summary?.date && (
                <div><span className="text-slate-500">Date:</span> {formatDateDMY(activeCancel.doc_summary.date)}</div>
              )}
              {activeCancel.doc_summary?.amount != null && (
                <div><span className="text-slate-500">Amount:</span> <span className="font-mono">{Number(activeCancel.doc_summary.amount).toFixed(3)}</span></div>
              )}
              <div><span className="text-slate-500">Requested by:</span> {activeCancel.requested_by_name || '—'}</div>
              <div><span className="text-slate-500">When:</span> {new Date(activeCancel.requested_at).toLocaleString()}</div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wide font-semibold text-slate-700">Reason for cancellation</div>
              <div className="text-sm text-slate-900 mt-1 whitespace-pre-wrap">{activeCancel.reason}</div>
              {activeCancel.note && (
                <div className="text-xs italic text-slate-600 mt-2">Note: {activeCancel.note}</div>
              )}
            </div>

            <div>
              <label className="text-xs text-slate-600">Rejection reason (only required if rejecting)</label>
              <textarea
                rows={2}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                placeholder="e.g. Already paid by customer — keep the invoice"
                data-testid="cancellation-reject-reason"
              />
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={rejectCancel}
                disabled={busy}
                className="px-3 py-1.5 text-sm rounded border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                data-testid="cancellation-reject-btn"
              >Reject Cancellation</button>
              <button
                onClick={approveCancel}
                disabled={busy}
                className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                data-testid="cancellation-approve-btn"
              >Approve Cancellation</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
